<template>
  <div class="flex h-full items-center justify-between bg-gray-700 px-4 shadow-md">
    <div class="w-1/6">
      <div class="flex justify-start">
        <button
          class="block h-8 w-8 rounded transition-colors duration-200 hover:bg-gray-600"
          @click="toggleThumbnails"
        >
          <i class="fa-solid fa-bars text-lg text-white" />
        </button>
      </div>
    </div>

    <div class="w-2/3">
      <div class="flex items-center justify-center space-x-4">
        <div :class="[arePagesControlDisabled && 'opacity-50']" class="flex items-center space-x-1">
          <div>
            <button
              :disabled="arePagesControlDisabled"
              class="flex-center h-8 w-8 rounded transition-colors duration-200 hover:bg-gray-600"
              @click="previousPage"
            >
              <i class="fa-solid fa-caret-left relative left-[-1px] text-lg text-white" />
            </button>
          </div>

          <div>
            <div class="text-white">{{ currentPage }} / {{ totalPage }}</div>
          </div>

          <div>
            <button
              :disabled="arePagesControlDisabled"
              class="block h-8 w-8 rounded transition-colors duration-200 hover:bg-gray-600"
              @click="nextPage"
            >
              <i class="fa-solid fa-caret-right relative left-[1px] text-lg text-white" />
            </button>
          </div>
        </div>

        <div class="h-4 border-l border-gray-400" />

        <div class="flex items-center space-x-1">
          <div>
            <button
              class="flex h-8 w-8 items-center justify-center rounded transition-colors duration-200 hover:bg-gray-600 disabled:opacity-50"
              @click="zoomOut"
            >
              <svg class="fill-white" height="20" viewBox="0 0 24 24" width="20">
                <g>
                  <path d="M19 13H5v-2h14v2z"></path>
                </g>
              </svg>
            </button>
          </div>

          <div>
            <div class="text-white">{{ formattedScale }}</div>
          </div>

          <div>
            <button
              class="flex h-8 w-8 items-center justify-center rounded transition-colors duration-200 hover:bg-gray-600 disabled:opacity-50"
              @click="zoomIn"
            >
              <svg class="fill-white" height="20" viewBox="0 0 24 24" width="20">
                <g>
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"></path>
                </g>
              </svg>
            </button>
          </div>
        </div>

        <div class="h-4 border-l border-gray-400"></div>

        <div>
          <button
            class="flex h-8 w-8 items-center justify-center rounded transition-colors duration-200 hover:bg-gray-600"
            @click="fitDocument"
          >
            <svg v-if="fitMode === 'width'" class="fill-white" height="20" viewBox="0 0 24 24" width="20">
              <g>
                <path
                  clip-rule="evenodd"
                  d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 10l3.01-4.5L15 10H9zm0 4h6l-2.99 4.5L9 14zm-6 5.01h18V4.99H3v14.02z"
                  fill-rule="evenodd"
                ></path>
              </g>
            </svg>

            <svg v-else-if="fitMode === 'height'" class="fill-white" height="20" viewBox="0 0 24 24" width="20">
              <g>
                <path
                  clip-rule="evenodd"
                  d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM3.5 12.01L8 9v6l-4.5-2.99zM16 15V9l4.5 3.01L16 15zM3 19.01h18V4.99H3v14.02z"
                  fill-rule="evenodd"
                ></path>
              </g>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div class="w-1/6">
      <div class="flex justify-end">
        <a
          v-if="detailPageUrl && scope !== 'input_file_detail' && enableDetailLink"
          v-tippy="$t('attributes_sidebar.open_in_new_page')"
          :href="detailPageUrl"
          class="flex-center h-8 w-8 rounded transition-colors duration-200 hover:bg-gray-600"
          target="_blank"
        >
          <i class="bi bi-box-arrow-up-right text-lg text-white" />
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { inject, computed, toRef } from "vue"
import { safeInject } from "../../../shared/helpers"

// props
const props = defineProps({
  detailPageUrl: String,
  highlightBlockIds: Array,
  highlightBlockScores: Array,
  enableDetailLink: {
    type: Boolean,
    default: true
  }
})

// inject
const { isOnlyShowAttributeSourcePages, attributeSources } = inject("VIEWER_STATE")

const {
  currentPage,
  totalPage,
  scale,
  fitMode,
  zoomOut,
  zoomIn,
  fitDocument,
  toggleThumbnails,
  previousPage,
  nextPage
} = inject("CONTROL_STATE")

// inject sidebar states
const attributeSidebarState = inject("ATTRIBUTE_SIDEBAR_PANEL_STATE")
const currentPanel = toRef(attributeSidebarState, "currentPanel")

// Safely inject all possible stores
const inputPreviewState = safeInject("INPUT_PREVIEW_STATE")
const projectsPreviewState = safeInject("PROJECTS_PREVIEW_STATE")

// Get the main state based on current panel
const mainStateMap = {
  fileAttributes: inputPreviewState,
  projectAttributes: projectsPreviewState
}
const mainState = mainStateMap[currentPanel.value] || {}

const scope = toRef(mainState, "scope")

const formattedScale = computed(() => {
  if (!scale.value) {
    return ""
  }

  return `${Math.round(scale.value * 100)}%`
})

// computed
const detailPageUrl = computed(() => {
  /*
   * In most cases the detailPageUrl that comes directly from the input preview result should be used.
   * However, this does not seem stable in some cases and lead to no highlight being shown.
   * Since we also pass the highlight block data as props, we can use this data to construct the URL.
   */
  let url = props.detailPageUrl

  const propsHighlightBlockIds = props.highlightBlockIds || []
  const propsHighlightBlockScores = props.highlightBlockScores || []
  const urlParams = new URLSearchParams(url.split("?")[1])
  const urlHighlightBlockIds = urlParams.getAll("highlight_block_ids[]")
  const urlChunkScores = urlParams.getAll("chunk_scores[]").map(score => parseFloat(score) || 0)

  // Case 1: If props highlight block data exists but none in the URL
  if (
    !url.includes("highlight_block_ids") &&
    !url.includes("chunk_scores") &&
    (propsHighlightBlockIds.length || propsHighlightBlockScores.length)
  ) {
    if (props.detailUrl) console.log("Detail URL does not include highlight block data, but props contain them.")
    propsHighlightBlockIds.forEach(id => {
      url += `highlight_block_ids[]=${encodeURIComponent(id)}&`
    })
    propsHighlightBlockScores.forEach(score => {
      url += `chunk_scores[]=${score || 0}&`
    })
    return url
  }

  // Case 2: Both URL and props contain highlight block data
  if (urlHighlightBlockIds.length && propsHighlightBlockIds.length) {
    const isSuperset =
      propsHighlightBlockIds.length >= urlHighlightBlockIds.length &&
      urlHighlightBlockIds.every(id => propsHighlightBlockIds.includes(id)) &&
      urlChunkScores.every(score => propsHighlightBlockScores.includes(score))

    if (!isSuperset) {
      console.log(
        "Props highlight block data is not a superset of the URL data. Props:",
        { ids: propsHighlightBlockIds, scores: propsHighlightBlockScores },
        "URL:",
        { ids: urlHighlightBlockIds, scores: urlChunkScores }
      )
    }

    if (propsHighlightBlockIds.length > urlHighlightBlockIds.length) {
      console.log("Using props highlight block data as it is a larger group.")
      urlHighlightBlockIds.forEach(() => {
        url = url.replace(/highlight_block_ids\[\]=[^&]+&?/, "")
      })
      urlChunkScores.forEach(() => {
        url = url.replace(/chunk_scores\[\]=[^&]+&?/, "")
      })

      propsHighlightBlockIds.forEach(id => {
        url += `highlight_block_ids[]=${encodeURIComponent(id)}&`
      })
      propsHighlightBlockScores.forEach(score => {
        url += `chunk_scores[]=${score || 0}&`
      })
      return url
    }
  }

  return url
})

const arePagesControlDisabled = computed(() => isOnlyShowAttributeSourcePages.value && attributeSources?.value?.length)
</script>
